/**
 * <Modal> — das Fundament fuer ALLE Overlays der Anwendung (Issue #67).
 *
 * WARUM ES DIESE KOMPONENTE GIBT (bitte vor dem naechsten Overlay lesen):
 *
 * Die Shell rendert ihren Inhalt in `<main class="… overflow-y-auto animate-fade-up">`
 * (App.tsx). `animate-fade-up` laeuft mit `animation-fill-mode: both`, der Endzustand
 * `transform: translateY(0)` bleibt also dauerhaft am Element haengen. Ein Element mit
 * `transform !== none` wird zum **Containing Block** fuer `position: fixed`-Nachfahren.
 * Ein `fixed inset-0`-Overlay INNERHALB von `<main>` bezieht sich damit nicht mehr aufs
 * Sichtfeld, sondern auf `<main>` — und weil `<main>` keine Hoehengrenze hat und mit dem
 * Inhalt waechst (gescrollt wird das Dokument, nicht `<main>`), spannt es sich ueber die
 * gesamte Inhaltshoehe und scrollt mit der Seite weg.
 *
 * Am laufenden System gemessen (Sichtfeld 1280x800): dasselbe Overlay in `<main>`
 * → top 62, Hoehe 1046. An `document.body` → top 0, Hoehe 800. Folge im ersten Fall:
 * `items-center` zentriert in der Mitte des GESAMTEN Listeninhalts (der Dialog rutscht
 * bei langen Listen aus dem Bild) und er scrollt mit dem Hintergrund weg.
 *
 * Ein Portal an `document.body` ist gegen diese ganze Fehlerklasse immun — unabhaengig
 * davon, welcher Vorfahre kuenftig ein `transform`, `filter` oder `contain` bekommt.
 * Deshalb entstehen Overlays ab jetzt ausschliesslich hier und nirgends sonst. Genau das
 * haelt `test/no-system-dialogs.test.ts` fest: `fixed inset-0` ist nur in dieser Datei
 * erlaubt.
 *
 * Was die Komponente zusichert:
 *   - Portal an `document.body` (nicht in den Seiteninhalt)
 *   - Scroll-Sperre im Hintergrund, solange offen
 *   - Escape schliesst (`closeOnEscape`), Klick auf den Hintergrund schliesst
 *     (`closeOnBackdrop`), Klick im Dialog nicht. Dialoge, die getippte Eingaben halten,
 *     schalten beide Wege ab — sonst verwirft ein Fehlgriff die Eingabe ohne Rueckfrage.
 *   - Fokus wandert beim Oeffnen in den Dialog, beim Schliessen zurueck auf den Ausloeser
 *     (mit Rueckfallebene, siehe `restoreFocus`)
 *   - Tab verlaesst den Dialog nicht
 *   - `role="dialog"` + `aria-modal`, Beschriftung ueber `label` oder `labelledBy`
 *   - Platzierung knapp oberhalb der Sichtfeldmitte
 *   - Verschachtelt: nur das oberste offene Modal reagiert auf Escape und Tab
 */
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Was als tastaturerreichbar gilt. Absichtlich ohne Sichtbarkeitspruefung
 * (`offsetParent`): jsdom rechnet kein Layout, die Pruefung waere dort immer falsch.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',');

function collectFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('inert') && element.tabIndex >= 0,
  );
}

/**
 * So lange nach dem Schliessen wird beobachtet, ob der Ausloeser noch existiert.
 * Grosszuegig genug fuer eine Server-Antwort, kurz genug, um nicht in eine spaetere,
 * unbeteiligte Aenderung hineinzugreifen.
 */
const TRIGGER_REMOVAL_WATCH_MS = 3000;

/**
 * Wohin der Fokus faellt, wenn der Ausloeser weg ist: auf den Hauptbereich der Shell.
 * `tabindex="-1"` macht ihn programmatisch fokussierbar, ohne ihn in die Tab-Reihenfolge
 * aufzunehmen. Ohne diesen Anker landet der Fokus auf `<body>` — und der naechste
 * Tab-Druck beginnt wieder ganz oben in der Navigation.
 */
function focusFallbackAnchor(): void {
  const anchor = document.querySelector('main');
  if (!anchor) return;
  if (!anchor.hasAttribute('tabindex')) anchor.setAttribute('tabindex', '-1');
  anchor.focus();
}

/**
 * Der Ausloeser verschwindet beim Loeschen NICHT beim Schliessen, sondern erst, wenn die
 * Mutation zurueckkommt — also nach der Fokus-Rueckgabe. Genau dann faellt der Fokus
 * still auf `<body>`. Deshalb wird das Verschwinden fuer eine begrenzte Zeit beobachtet
 * und der Fokus nachgezogen — aber nur, wenn er zwischenzeitlich niemand anders
 * uebernommen hat (sonst wuerde der Beobachter dem Nutzer den Fokus wegreissen).
 */
function watchTriggerRemoval(trigger: HTMLElement): void {
  if (document.activeElement !== trigger) return;
  const observer = new MutationObserver(() => {
    if (trigger.isConnected) return;
    stop();
    const active = document.activeElement;
    if (active === null || active === document.body) focusFallbackAnchor();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  const timer = setTimeout(() => observer.disconnect(), TRIGGER_REMOVAL_WATCH_MS);

  function stop(): void {
    observer.disconnect();
    clearTimeout(timer);
  }
}

/** Fokus-Rueckgabe beim Schliessen — mit Rueckfallebene statt ins Leere. */
function restoreFocus(trigger: HTMLElement | null): void {
  if (!trigger?.isConnected) {
    // Der Ausloeser war schon beim Schliessen weg (z. B. Liste hat sich neu geladen).
    focusFallbackAnchor();
    return;
  }
  trigger.focus();
  watchTriggerRemoval(trigger);
}

/**
 * Scroll-Sperre mit Zaehler: zwei gleichzeitig offene Modals duerfen sich nicht
 * gegenseitig entsperren. Der Zaehler ist bewusst modulglobal — es gibt genau ein
 * `document.body` pro Dokument.
 */
let scrollLockCount = 0;
let bodyOverflowBeforeLock = '';

/** Attribut am Body, ueber das `styles/tokens/base.css` auch `<main>` anhaelt. */
export const MODAL_OPEN_ATTRIBUTE = 'data-modal-open';

/**
 * Stapel offener Modals (PV-052 L5, #108). Aus dem Projekt-Popup gehen weitere Dialoge auf
 * (Phasenwechsel, Rückfragen). Alle hören auf `keydown` am document — ohne Stapel schlösse
 * Escape beide, und die Fokus-Falle des unteren zöge den Fokus bei jedem Tab aus dem oberen
 * heraus. Tastatur gehört deshalb nur dem obersten. Modulglobal wie die Scroll-Sperre.
 */
const modalStapel: symbol[] = [];

function istOberstes(id: symbol): boolean {
  return modalStapel[modalStapel.length - 1] === id;
}

function lockBackgroundScroll(): () => void {
  if (scrollLockCount === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.setAttribute(MODAL_OPEN_ATTRIBUTE, 'true');
  }
  scrollLockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLockCount -= 1;
    if (scrollLockCount === 0) {
      document.body.style.overflow = bodyOverflowBeforeLock;
      document.body.removeAttribute(MODAL_OPEN_ATTRIBUTE);
    }
  };
}

export type ModalProps = {
  /** Solange `false`, wird nichts gerendert und nichts gesperrt. */
  open: boolean;
  /** Escape, Hintergrundklick und die Schliessen-Bedienelemente des Inhalts rufen das auf. */
  onClose: () => void;
  /** Beschriftung, wenn der Inhalt keinen referenzierbaren Titel hat. */
  label?: string | undefined;
  /** `id` des Titelelements im Inhalt — hat Vorrang vor `label`. */
  labelledBy?: string | undefined;
  /** Breiten-/Layoutklassen des Dialogfensters. Default: schmal und mittig. */
  panelClassName?: string | undefined;
  /** Zusaetzliche Inline-Styles am Dialogfenster (ueberschreiben die Vorgaben). */
  panelStyle?: CSSProperties | undefined;
  /** Hintergrundklick schliesst. Nur abschalten, wenn ein Verlust drohen wuerde. */
  closeOnBackdrop?: boolean | undefined;
  /**
   * Escape schliesst. Zusammen mit `closeOnBackdrop` abschalten, sobald der Dialog
   * getippte Eingaben haelt — sonst ist der Verlust nur halb verhindert. Der Dialog
   * braucht dann einen sichtbaren Abbrechen-/Schliessen-Knopf.
   */
  closeOnEscape?: boolean | undefined;
  children: ReactNode;
};

export function Modal({
  open,
  onClose,
  label,
  labelledBy,
  panelClassName,
  panelStyle,
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const stapelId = useRef(Symbol('modal'));

  useEffect(() => {
    if (!open) return;
    const id = stapelId.current;
    modalStapel.push(id);
    return () => {
      const i = modalStapel.lastIndexOf(id);
      if (i >= 0) modalStapel.splice(i, 1);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    return lockBackgroundScroll();
  }, [open]);

  // Fokus rein — und beim Schliessen zurueck auf das ausloesende Element.
  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel) {
      const first = collectFocusable(panel)[0];
      (first ?? panel).focus();
    }
    return () => restoreFocus(trigger);
  }, [open]);

  // Escape schliesst; Tab bleibt im Dialog.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      // Nur das oberste Modal bedient die Tastatur (Stapel, siehe oben).
      if (!istOberstes(stapelId.current)) return;
      if (event.key === 'Escape') {
        // Abgeschaltet bei Dialogen mit Eingaben — dort bleibt der Dialog stehen,
        // geschlossen wird ueber Abbrechen. Kein `preventDefault()`, damit ein
        // offenes Auswahlfeld o. Ae. sein Escape behalten kann.
        if (!closeOnEscape) return;
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = collectFocusable(panel);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        // Nichts Bedienbares drin: Fokus bleibt am Dialogfenster selbst.
        event.preventDefault();
        panel.focus();
        return;
      }
      const active = document.activeElement;
      const insidePanel = active instanceof Node && panel.contains(active);
      if (event.shiftKey && (active === first || !insidePanel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !insidePanel)) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, closeOnEscape]);

  if (!open) return null;

  return createPortal(
    <div
      data-modal-overlay="true"
      className="fixed inset-0 z-[90] overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.5)', overscrollBehavior: 'contain' }}
    >
      {/*
        Platzierung: `min-h-full` + `items-center` zentriert im Sichtfeld, das
        zusaetzliche `pb-[14vh]` hebt den Dialog knapp darueber (bei 800px Hoehe rund
        48px). `mousedown` statt `click` fuers Schliessen — sonst schliesst auch eine
        Textmarkierung, die im Dialog beginnt und auf dem Hintergrund endet. Der
        Vergleich `target === currentTarget` trifft nur die Flaeche neben dem Dialog,
        ein Klick IM Dialog kommt hier gar nicht an.
      */}
      <div
        className="flex min-h-full items-center justify-center px-4 pt-4 pb-[14vh]"
        onMouseDown={(event) => {
          if (closeOnBackdrop && event.target === event.currentTarget) onClose();
        }}
      >
        {/*
          Kein `<dialog>`: dessen modales Verhalten haengt an `showModal()`, und das
          gibt es in der Testumgebung nicht (`typeof dialog.showModal === 'undefined'`
          in jsdom 25 nachgemessen). Genau die Zusicherungen, um die es in diesem
          Issue geht — Fokus, Scroll-Sperre, Escape — waeren damit nicht mehr
          pruefbar. Deshalb `role="dialog"` von Hand, mit allem, was dazugehoert.
        */}
        <div
          ref={panelRef}
          // biome-ignore lint/a11y/useSemanticElements: <dialog> braucht showModal(), das jsdom nicht hat — siehe Begruendung oben
          role="dialog"
          aria-modal="true"
          {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
          {...(label ? { 'aria-label': label } : {})}
          tabIndex={-1}
          className={`rounded-xl ${panelClassName ?? 'w-full max-w-md overflow-hidden'}`}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line-soft)',
            boxShadow: 'var(--shadow-lift, 0 20px 60px rgba(0,0,0,0.35))',
            outline: 'none',
            ...panelStyle,
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
